// prettier-ignore
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SelectedWorkspaceService } from '@cloud-twin-v2/cloud-twin-shared/selected-workspace.service';
import {
  AttributeInfoDto,
  CtWorkspaceDto,
  WorkspaceConnectSettingDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { LayerItemV2 } from '@common-modules/wlm-map/generic-map/models/layer-item';
import { MapTooltipParams } from '@common-modules/wlm-map/generic-map/models/map-tooltip-params';
import { NativeLayer } from '@common-modules/wlm-map/generic-map/models/native-layer';
import { GenericMapDataService } from '@common-modules/wlm-map/generic-map/services/generic-map-data.service';
import { MapLogService } from '@common-modules/wlm-map/generic-map/services/map-log.service';
import { Observable, ReplaySubject, Subscription } from 'rxjs';

export class CtMapTooltipParams extends MapTooltipParams { }

enum BasePropertyKeys {
  NetworkElementName = 'networkElementName',
  NetworkElementType = 'neType',
}

@Component({
  selector: 'ct-map-tooltip',
  templateUrl: './ct-map-tooltip.component.html',
  styleUrl: './ct-map-tooltip.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CtMapTooltipComponent implements OnInit {
  readonly update$ = new ReplaySubject<CtMapTooltipParams>(1);

  private _params: CtMapTooltipParams;
  get params(): CtMapTooltipParams {
    return this._params;
  }
  @Input() set params(value: CtMapTooltipParams) {
    this._params = value;
    this.layerItem = this.params.layerItem;
    this.nativeLayer = this.params.nativeLayer;
    this.thematicAttributeFields = this.getThematicAttributeFields(this.params.allLayerItems, this.params.layerItem);
    this.propertyKeys = this.propertyKeys.concat(this.thematicAttributeFields);
    this.allLayerItems$ = this.params.layerItemsChange$;

    this.logTooltipData();
  }

  @Input() layerItem: LayerItemV2;

  private _nativeLayer: NativeLayer;
  @Input() set nativeLayer(value: NativeLayer) {
    this._nativeLayer = value;
    this.properties = this.getFormattedProperties();
    this.baseProperties = this._nativeLayer?.feature.properties;
    this.labelProperties = this.getLabelProperties();
    this.propertyKeys = this.basePropertyKeys.concat(this.connectKeys);

    if (this.properties) {
      this.fillConnectionData();
    }

    this.elementName = this.getElementName();
  }

  private _allLayerItems$: Observable<LayerItemV2[]>;
  get allLayerItems$(): Observable<LayerItemV2[]> {
    return this._allLayerItems$;
  }
  set allLayerItems$(value: Observable<LayerItemV2[]>) {
    this._allLayerItems$ = value;

    this._allLayerItemsSubscription?.unsubscribe();
    this._allLayerItemsSubscription = this._allLayerItems$?.subscribe((layerItems) => {
      //this.step = layerItems;
      this._cd.detectChanges();
    });
  }

  thematicAttributeFields: string[];
  properties: { [key: string]: string } = null;
  baseProperties: { [key: string]: string };
  private _allLayerItemsSubscription: Subscription;
  private _labelProperties: { [key: string]: AttributeInfoDto } = {};
  public get labelProperties(): { [key: string]: AttributeInfoDto } {
    return this._labelProperties;
  }
  public set labelProperties(value: { [key: string]: AttributeInfoDto }) {
    this._labelProperties = value;

    this.labelPropertiesMap = this._labelProperties
      ? Object.entries(this._labelProperties).reduce(
        (acc, [key, attributeInfo]) => {
          if (attributeInfo.alias) {
            acc[key] = attributeInfo.alias;
          }
          return acc;
        },
        {} as { [key: string]: string },
      )
      : {};
  }

  labelPropertiesMap: { [key: string]: string } = {};
  propertyKeys = [];
  elementName: string;
  connectSettings: WorkspaceConnectSettingDto;
  selectedWorkspace: CtWorkspaceDto;

  readonly BasePropertyKeysEnum = BasePropertyKeys;
  readonly basePropertyKeys: string[] = [
    BasePropertyKeys.NetworkElementName,
    BasePropertyKeys.NetworkElementType,
  ];
  readonly hiddenPropertyKeys = ['neId', 'scenarioId', 'model', 'modelVersion', 'scenarioName'];
  readonly connectKeys = ['model', 'modelVersion', 'scenarioName'];
  id: string;
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _cd = inject(ChangeDetectorRef);

  constructor(
    private readonly _selectedWorkspaceService: SelectedWorkspaceService,
    private readonly _genericMapDataService: GenericMapDataService,
    private readonly _log: MapLogService,
  ) {
    this.id = globalUtilsHelper.generateGuid();
    this._selectedWorkspaceService.selectedWorkspace$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((workspace) => {
        this.selectedWorkspace = workspace;
      });
  }

  ngOnInit(): void {
    this.update$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((params) => {
      this.params = params;
      this.id = globalUtilsHelper.generateGuid();
      this._cd.detectChanges();
    });
  }

  private fillConnectionData(): void {
    const scenarioId = this.properties['scenarioId'];
    this.connectSettings = this.selectedWorkspace.workspaceConnectSettings.find(
      (x) => x.scenarioId === scenarioId,
    );

    if (this.connectSettings) {
      this.properties['model'] = this.connectSettings.instanceName;
      this.properties['modelVersion'] = this.connectSettings.snapshotName;
      this.properties['scenarioName'] = this.connectSettings.scenarioName;

      this.labelPropertiesMap['model'] = 'Model';
      this.labelPropertiesMap['modelVersion'] = 'Model Version';
      this.labelPropertiesMap['scenarioName'] = 'Scenario';

      this._cd.detectChanges();
    }
  }

  private getThematicAttributeFields(allLayerItems: LayerItemV2[], layerItem: LayerItemV2): string[] {
    const currentType = layerItem.originalFiltrableProperties[BasePropertyKeys.NetworkElementType]
    const thematicLayers = allLayerItems.filter(f => f.originalFiltrableProperties[BasePropertyKeys.NetworkElementType] === currentType && f.layerDefinition.isThematic && f.isVisible);

    const thematicAttributeFieldNames: string[] = [];
    thematicLayers.forEach(thematicLayer => {
      thematicAttributeFieldNames.push(thematicLayer.layerStyle.fieldName);
    });

    return [...new Set(thematicAttributeFieldNames)];

  }

  private logTooltipData(): void {
    this._log.log('Map tooltip: Opening with data: ', {
      elementName: this.elementName,
      formattedProperties: this.properties,
      baseProperties: this.baseProperties,
      labelProperties: this.labelProperties,
      layerItem: this.layerItem,
    });
  }

  private getFormattedProperties(): { [key: string]: string } {
    const getInnerLayerId = this.params.layerItemService.getInnerLayerId;
    return this._genericMapDataService.getFormattedProperties(this._nativeLayer, getInnerLayerId);
  }

  private getLabelProperties(): { [key: string]: AttributeInfoDto } {
    const getInnerLayerId = this.params.layerItemService.getInnerLayerId;
    return this._genericMapDataService.getLabelProperties(this._nativeLayer, getInnerLayerId);
  }

  private getElementName(): string {
    const fieldName = BasePropertyKeys.NetworkElementName;
    if (this.properties && this.properties[fieldName]) {
      return this.properties[fieldName];
    }

    if (this.baseProperties && this.baseProperties[fieldName]) {
      return this.baseProperties[fieldName];
    }

    return this.layerItem?.layerName;
  }
}
